import React, { useState, useRef, useEffect, useCallback } from "react";
import Modal from "react-modal";
import config from "../configs/config";
import { useAuth } from "../providers/AuthProvider";
import { useToast } from "../contexts/ToastContext";
import CommonButton from "../elments/CommonButton";
import SingleResponsePreview from "./Survey/SingleResponsePreview";
import { useUndoRedo } from "./hooks/useUndoRedo";
import { faRotateLeft, faRotateRight, faEye } from "@fortawesome/free-solid-svg-icons";
import { normalizeRequiredFlags } from "../utils/surveyNormalization";
import "../../src/components/styles/SurveyPreview.css";
import "./styles/AiReviewModal.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axios from "axios";
import ISO6391 from "iso-639-1";
import {
  FaCode,
  FaCheckCircle,
  FaTable,
  FaList,
  FaCalculator,
  FaChartBar,
  FaThLarge,
  FaHome,
  FaClipboardList,
  FaInfoCircle,
  FaEdit,
  FaTimes,
  FaSearch,
  FaCopy,
  FaChevronDown,
  FaChevronUp,
  FaQuestionCircle,
  FaListCheck,
  FaExclamationTriangle,
  FaFileAlt,
  FaUndo,
  FaRedo,
  FaMicrophone,
  FaStop,
  FaSave,
  FaEye,
  FaDownload,
  FaLanguage,
  FaRobot,
  FaComments,
  FaCheck,
  FaPlus,
  FaMinus,
  FaTrash,
  FaArrowLeft,
  FaArrowRight,
  FaFilePdf,
  FaFileWord,
  FaFileImage,
  FaFileCode,
} from "react-icons/fa";
import { validateSurvey } from "../utils/surveyValidation";
import { diffDataGroups, formatOp, stripHtml } from "../utils/changeLogUtils";
import { splitDiffRows } from "../utils/lineDiff";

const SurveyPreview = ({
  sdataGroup: initialSdataGroup,
  surveyName,
  surveyFileName,
  oeParameters,
  onShowToastMessage,
  onSetProcessingMessage,
  questionnaireFileId,
  exportedFiles = {},
  projectId,
  projectName,
  selectedClient,
  selectedProject,
  onSurveyUpdate,
  onPersistChangeLog,
  powner,
  pid,
  pname
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState("questions");
  const [sdataGroup, setSdataGroup] = useState(initialSdataGroup);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [showSplitView, setShowSplitView] = useState(false);
  const [isLoadingDocx, setIsLoadingDocx] = useState(false);
  const [splitRatio, setSplitRatio] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedExport, setSelectedExport] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [allowedDownloadFeatures, setAllowedDownloadFeatures] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  // AI Translation section's language picker - deliberately NEVER
  // auto-populated from supportedLanguages (unlike manageSelectedLanguages
  // below), so it always starts empty: this is an ad-hoc "translate these
  // right now" pick, not a reflection of what's already configured.
  const [aiSelectedLanguages, setAiSelectedLanguages] = useState([]);
  const [aiLanguageSearch, setAiLanguageSearch] = useState("");
  // Manage Translation section's language picker - pre-checked from
  // supportedLanguages (see loadLanguageConfig effect below) so it shows
  // what's already saved, ready to Save-as-Supported/Download/Upload again.
  const [manageSelectedLanguages, setManageSelectedLanguages] = useState([]);
  const [manageLanguageSearch, setManageLanguageSearch] = useState("");
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  // ─── Translations (persisted server-side, ERA Final owns this entirely) ───
  // { [language]: { label, translations: { Question_number: {title, rows:{idx:text}, columns:{idx:text}} } } }
  const [translationsByLang, setTranslationsByLang] = useState({});
  const [activeTranslationLang, setActiveTranslationLang] = useState(null); // null = original/default text
  const [isUploadingTranslations, setIsUploadingTranslations] = useState(false);
  const translationUploadInputRef = useRef(null);

  // Languages this survey supports, as configured by the researcher
  // (analogous to redirects) - drives the respondent-facing language
  // switcher, independent of which languages actually have translated
  // text uploaded yet (a supported-but-not-yet-translated language just
  // falls back to the original text - see applyTranslation()).
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [isSavingLanguages, setIsSavingLanguages] = useState(false);

  // Per-question comments, master comment & AI review states
  const [comments, setComments] = useState({});
  const [masterComment, setMasterComment] = useState("");
  const [isSubmittingToAI, setIsSubmittingToAI] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [aiChanges, setAiChanges] = useState([]);
  const [aiSubmitMessage, setAiSubmitMessage] = useState(null);
  const [aiSubmitAttempts, setAiSubmitAttempts] = useState(0);

  // "Upload latest QNR" - diff a revised doc against the live survey.
  // pendingQnrJson/pendingQnrFilename ride alongside aiChanges so the Apply
  // step can persist them as the next diff's base (see qnr-diff-apply).
  const [isUploadingQnr, setIsUploadingQnr] = useState(false);
  const [pendingQnrJson, setPendingQnrJson] = useState(null);
  const [pendingQnrFilename, setPendingQnrFilename] = useState(null);
  const qnrUploadInputRef = useRef(null);

  // Editable Raw-JSON view state
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState(null);

  // convert to array
  const languages = ISO6391.getAllCodes().map((code) => ({
    code,
    label: ISO6391.getName(code),
  }));

  const recognitionRef = useRef(null);
  const containerRef = useRef(null);

  // ─── Load supported languages + persisted translations alongside the
  // question data, whenever the preview opens - so a respondent can switch
  // languages seamlessly from the first render, no extra round-trip mid-survey ───
  useEffect(() => {
    if (!isPreviewOpen || !projectId) return;

    const loadLanguageConfig = async () => {
      try {
        const res = await fetch(`${config.API_BASE_URL}/get-language-config/${projectId}`, {
          credentials: 'include',
        });
        const result = await res.json();
        const langs = result.success ? (result.languages || []) : [];
        setSupportedLanguages(langs);
        setManageSelectedLanguages(langs); // pre-check whatever's already configured
        setAiSelectedLanguages([]); // AI section always starts unselected
      } catch (err) {
        console.error('Failed to load language config:', err);
        setSupportedLanguages([]);
      }
    };

    const loadTranslations = async () => {
      try {
        const res = await fetch(`${config.API_BASE_URL}/get-translations/${projectId}`, {
          credentials: 'include',
        });
        const result = await res.json();
        setTranslationsByLang(result.success && result.translations ? result.translations : {});
      } catch (err) {
        console.error('Failed to load translations:', err);
        setTranslationsByLang({});
      }
      setActiveTranslationLang(null); // always reopen on the original text
    };

    loadLanguageConfig();
    loadTranslations();
  }, [isPreviewOpen, projectId]);

  // Returns a COPY of `question` with Qtitle/row text/column text swapped for
  // the active language's translation, falling back to the original string
  // for anything not (yet) translated. Used only at render time - never
  // mutates sdataGroup, so switching languages is instant and lossless.
  const applyTranslation = (question) => {
    if (!question || !activeTranslationLang) return question;
    const lang = translationsByLang[activeTranslationLang];
    const qMap = lang?.translations?.[question.Question_number];
    if (!qMap) return question;
    const translated = { ...question };
    if (qMap.title) translated.Qtitle = qMap.title;
    if (question.rows && qMap.rows) {
      translated.rows = question.rows.map((r) => ({
        ...r,
        text: qMap.rows[String(r.index)] || r.text,
      }));
    }
    if (question.columns && qMap.columns) {
      translated.columns = question.columns.map((c) => ({
        ...c,
        text: qMap.columns[String(c.index)] || c.text,
      }));
    }
    return translated;
  };

  // ========== UNDO / REDO (for live JSON editing in the preview) ==========
  const { undo, redo, canUndo, canRedo } = useUndoRedo({
    state: sdataGroup,
    setState: setSdataGroup,
    onRestore: (restored) => {
      if (previewType === "json") {
        setJsonText(JSON.stringify(restored ?? {}, null, 2));
        setJsonError(null);
      }
      if (onSurveyUpdate) onSurveyUpdate(restored);
      setHasUnsavedChanges(true);
    },
    resetKey: `${projectId}:${isPreviewOpen}`,
    debounceMs: 400,
    maxHistory: 100,
  });

  // Seed the editable JSON text whenever the Raw JSON view is opened.
  useEffect(() => {
    if (previewType === "json") {
      setJsonText(JSON.stringify(sdataGroup ?? {}, null, 2));
      setJsonError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewType]);

  // Live-edit handler for the Raw JSON textarea.
  const handleJsonEdit = (value) => {
    setJsonText(value);
    try {
      const parsed = JSON.parse(value);
      setJsonError(null);
      setSdataGroup(parsed);
      if (onSurveyUpdate) onSurveyUpdate(parsed);
      setHasUnsavedChanges(true);
    } catch (err) {
      setJsonError(err.message);
    }
  };

  // Copy the full raw JSON to the clipboard.
  const [jsonCopied, setJsonCopied] = useState(false);
  const handleCopyJson = async () => {
    const text = jsonText || JSON.stringify(sdataGroup ?? {}, null, 2);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setJsonCopied(true);
      setTimeout(() => setJsonCopied(false), 1500);
    } catch (err) {
      showToastMessage("Failed to copy JSON");
    }
  };

  // Undo/redo keyboard shortcuts
  useEffect(() => {
    if (!isPreviewOpen) return undefined;
    const handleKeyDown = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        redo();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isPreviewOpen, undo, redo]);

  const isFirst = currentIndex === 0;
  const isLast = currentIndex >= (sdataGroup ? Object.keys(sdataGroup).length - 1 : 0);

  const handleNext = () => !isLast && setCurrentIndex((prev) => prev + 1);
  const handleBack = () => !isFirst && setCurrentIndex((prev) => prev - 1);

  const currentQuestion = sdataGroup ? Object.values(sdataGroup)[currentIndex] : null;

  // Sync sdataGroup when prop changes & initialise speech recognition
  useEffect(() => {
    setSdataGroup(initialSdataGroup);
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";
      recognitionRef.current.onstart = () => setTranscript("Listening...");
      recognitionRef.current.onresult = (event) => {
        if (isSpeaking) return;
        const command = event.results[0][0].transcript.toLowerCase().trim();
        if (command.length < 3) return;
        setTranscript(command);
        handleVoiceCommand(command);
      };
      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        if (event.error !== "aborted" && event.error !== "not-allowed") {
          setTranscript(`Error: ${event.error}`);
          speak("Try again.");
        }
        setIsListening(false);
      };
      recognitionRef.current.onend = () => {
        setIsListening(false);
        setTranscript("");
      };
    }
  }, [initialSdataGroup, isSpeaking]);

  // Load DOCX preview when split view is enabled
  useEffect(() => {
    if (showSplitView && !embedUrl && questionnaireFileId) {
      loadDocxPreview();
    }
  }, [showSplitView]);

  useEffect(() => {
    const organization = user?.user?.organization || user?.organization;
    if (organization) {
      if (
        organization.download_features &&
        Array.isArray(organization.download_features) &&
        organization.download_features.length > 0
      ) {
        setAllowedDownloadFeatures(organization.download_features);
      } else {
        setAllowedDownloadFeatures([]);
      }
    } else {
      setAllowedDownloadFeatures([]);
    }
  }, [user]);

  const hasDownloadFeatureAccess = (feature) =>
    allowedDownloadFeatures.includes(feature);

  const showToastMessage = (message, type = 'info') => {
    if (onShowToastMessage) {
      onShowToastMessage(message);
    } else {
      const fn = toast[type] ?? toast.info;
      fn(message);
    }
  };

  const setProcessingMessage = (message) => {
    if (onSetProcessingMessage) onSetProcessingMessage(message);
  };

  // ─── Document preview ───────────────────────────────────────────────────────
  const loadDocxPreview = async () => {
    if (!questionnaireFileId) {
      showToastMessage("No document available for preview");
      return;
    }
    try {
      setIsLoadingDocx(true);
      setProcessingMessage("Loading document preview...");
      const response = await fetch(`${config.API_BASE_URL}/get_embed_url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          onedrive_file_id: questionnaireFileId,
          surveyFileName,
          powner,
        }),
      });
      if (!response.ok) throw new Error("Failed to get embed URL");
      const data = await response.json();
      if (data.success) {
        setEmbedUrl(data.embed_url);
      } else {
        showToastMessage("Failed to load document preview");
        setShowSplitView(false);
      }
    } catch (error) {
      console.error("Error previewing document:", error);
      showToastMessage("Error loading document preview");
      setShowSplitView(false);
    } finally {
      setIsLoadingDocx(false);
    }
  };

  // ─── AI review: submit questionnaire + comments ──────────────────────────────
  const handleSubmitToAI = async () => {
    if (aiSubmitAttempts >= 3) {
      setAiSubmitMessage({ success: false, text: "Maximum 3 attempts reached for this session. Please close and reopen the preview to reset." });
      return;
    }

    if (aiSubmitAttempts === 0) {
      const proceed = window.confirm(
        "Attempt 1 of 3\n\nHave you completed all your review comments? AI produces the best results when you send all your feedback in one comprehensive batch.\n\nAre you sure you want to submit now?"
      );
      if (!proceed) return;
    } else if (aiSubmitAttempts === 1) {
      const proceed = window.confirm(
        "Attempt 2 of 3\n\nThis is your second submission. Make sure you have added all remaining comments before proceeding — you have only 1 attempt left after this.\n\nAre you sure you want to submit now?"
      );
      if (!proceed) return;
    }

    try {
      setIsSubmittingToAI(true);
      setAiSubmitMessage(null);

      const reviewItems = Object.entries(sdataGroup)
        .filter(([key]) => comments[key]?.trim())
        .map(([key, question]) => ({
          key,
          question,
          comment: comments[key].trim(),
        }));

      const allQuestions = Object.entries(sdataGroup)
        .map(([key, q]) => ({
          key,
          Question_number: q.Question_number || key,
          type: q.type || "",
          _order: q._order || 0,
          title_short: (q.Qtitle || "").replace(/<[^>]+>/g, "").substring(0, 60),
        }))
        .sort((a, b) => a._order - b._order);

      const response = await fetch(
        `${config.API_BASE_URL}/api/ai/review-with-comments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            reviewItems,
            masterComment: masterComment.trim(),
            allQuestions,
            surveyFileName,
            questionnaireFileId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get response from AI");
      }

      const data = await response.json();
      const changes = [];

      if (data.updatedQuestions) {
        Object.entries(data.updatedQuestions).forEach(([key, updated]) => {
          changes.push({
            questionKey: key,
            original: sdataGroup[key] || null,
            updated,
            isNew: false,
            status: "pending",
          });
        });
      }

      if (data.newQuestions?.length > 0) {
        data.newQuestions.forEach((nq, idx) => {
          const q = nq.question || {};
          const qKey = q.Question_number || `NEW_Q_${idx + 1}`;
          changes.push({
            questionKey: qKey,
            original: null,
            updated: q,
            isNew: true,
            insertAfterKey: nq.insertAfter || null,
            insertBeforeKey: nq.insertBefore || null,
            status: "pending",
          });
        });
      }

      if (changes.length > 0) {
        setAiChanges(changes);
        setShowReviewModal(true);
      } else {
        setAiSubmitMessage({
          success: false,
          text: data.message || "AI processed the request but no changes were proposed.",
        });
      }
      setAiSubmitAttempts((prev) => prev + 1);
    } catch (error) {
      console.error("AI submit error:", error);
      setAiSubmitMessage({ success: false, text: `Error: ${error.message}` });
      setAiSubmitAttempts((prev) => prev + 1);
    } finally {
      setIsSubmittingToAI(false);
    }
  };

  // ─── Upload latest QNR: diff against live survey, reuse the review modal ────
  const handleUploadQnrFile = (e) => {
    const file = e.target.files[0];
    if (file) handleUploadQnrDiff(file);
    if (qnrUploadInputRef.current) qnrUploadInputRef.current.value = "";
  };

  const handleUploadQnrDiff = async (file) => {
    setIsUploadingQnr(true);
    setAiSubmitMessage(null);
    try {
      const formData = new FormData();
      formData.append("questionnaire", file);
      formData.append("projectId", projectId);

      const response = await fetch(`${config.API_BASE_URL}/api/ai/qnr-diff`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to diff questionnaire");
      }

      const data = await response.json();
      const diff = data.diff || {};
      const changes = [];

      Object.entries(diff.updatedQuestions || {}).forEach(([key, updated]) => {
        changes.push({
          questionKey: key,
          original: sdataGroup[key] || null,
          updated,
          isNew: false,
          status: "pending",
          source: "qnr",
        });
      });

      Object.entries(diff.possiblyRemoved || {}).forEach(([key, entry]) => {
        changes.push({
          questionKey: key,
          original: entry.question,
          updated: { _deleted: true },
          isNew: false,
          status: "pending",
          source: "qnr",
          suggestedNewKey: entry.suggestedNewKey || null,
        });
      });

      Object.entries(diff.conflicts || {}).forEach(([key, c]) => {
        changes.push({
          questionKey: key,
          original: c.ours,
          updated: c.theirs,
          isNew: false,
          status: "pending",
          source: "qnr",
          conflict: true,
          baseValue: c.base,
        });
      });

      (diff.newQuestions || []).forEach((nq, idx) => {
        const q = nq.question || {};
        const qKey = q.Question_number || `NEW_Q_${idx + 1}`;
        changes.push({
          questionKey: qKey,
          original: null,
          updated: q,
          isNew: true,
          insertAfterKey: nq.insertAfter || null,
          insertBeforeKey: null,
          status: "pending",
          source: "qnr",
        });
      });

      setPendingQnrJson(data.newQnrJson || null);
      setPendingQnrFilename(data.sourceFilename || file.name);

      if (changes.length > 0) {
        setAiChanges(changes);
        setShowReviewModal(true);
      } else {
        setAiSubmitMessage({
          success: true,
          text: "Compared against the current survey - no differences found.",
        });
      }
    } catch (error) {
      console.error("QNR diff error:", error);
      setAiSubmitMessage({ success: false, text: `Error: ${error.message}` });
    } finally {
      setIsUploadingQnr(false);
    }
  };

  // ─── AI review modal: accept / reject helpers ────────────────────────────────
  const acceptChange = (questionKey) =>
    setAiChanges((prev) =>
      prev.map((c) =>
        c.questionKey === questionKey ? { ...c, status: "accepted" } : c
      )
    );

  const rejectChange = (questionKey) =>
    setAiChanges((prev) =>
      prev.map((c) =>
        c.questionKey === questionKey ? { ...c, status: "rejected" } : c
      )
    );

  const acceptAllChanges = () =>
    setAiChanges((prev) => prev.map((c) => ({ ...c, status: "accepted" })));

  const rejectAllChanges = () =>
    setAiChanges((prev) => prev.map((c) => ({ ...c, status: "rejected" })));

  const applySelectedChanges = async () => {
    const accepted = aiChanges.filter((c) => c.status === "accepted");
    if (accepted.length > 0) {
      const newSdataGroup = { ...sdataGroup };
      let deletedCount = 0;
      let modifiedCount = 0;
      let newCount = 0;

      accepted.forEach(({ questionKey, updated: upd, isNew }) => {
        if (upd?._deleted) {
          delete newSdataGroup[questionKey];
          deletedCount++;
        } else if (isNew) {
          const finalKey = upd.Question_number || questionKey;
          newSdataGroup[finalKey] = upd;
          newCount++;
        } else {
          newSdataGroup[questionKey] = upd;
          modifiedCount++;
        }
      });

      // QNR-sourced changes need to persist server-side immediately: that's
      // what snapshots the pre-update sdataGroup (the version-switch revert
      // point) and stores the parsed doc as the base for the NEXT upload's
      // 3-way diff. Wait for that to succeed before touching local state,
      // so the UI never shows something the version history doesn't know about.
      const isQnrSourced = accepted.some((c) => c.source === "qnr");
      if (isQnrSourced) {
        try {
          const response = await fetch(`${config.API_BASE_URL}/api/ai/qnr-diff-apply`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              projectId,
              mergedSdataGroup: newSdataGroup,
              newQnrJson: pendingQnrJson,
              sourceFilename: pendingQnrFilename,
            }),
          });
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Failed to apply QNR update");
          }
          setPendingQnrJson(null);
          setPendingQnrFilename(null);
        } catch (error) {
          console.error("QNR diff apply error:", error);
          setAiSubmitMessage({ success: false, text: `Error applying QNR update: ${error.message}` });
          return;
        }
      }

      setSdataGroup(newSdataGroup);
      // {source:'ai'} tells SurveyBuilder to tag the resulting change-log
      // entry "[AI]" so it's distinguishable from a manual edit.
      if (onSurveyUpdate) onSurveyUpdate(newSdataGroup, { source: "ai" });
      setHasUnsavedChanges(true);

      const parts = [];
      if (modifiedCount > 0) parts.push(`${modifiedCount} modified`);
      if (deletedCount > 0) parts.push(`${deletedCount} deleted`);
      if (newCount > 0) parts.push(`${newCount} added`);

      setAiSubmitMessage({
        success: true,
        text: `Applied AI changes: ${parts.join(", ")} successfully.`,
      });
      setComments({});
      setMasterComment("");
    }
    setShowReviewModal(false);
    setAiChanges([]);
  };

  const handleAiLanguageChange = (lang) => {
    setAiSelectedLanguages((prev) =>
      prev.includes(lang)
        ? prev.filter((l) => l !== lang)
        : [...prev, lang]
    );
  };

  const handleManageLanguageChange = (lang) => {
    setManageSelectedLanguages((prev) =>
      prev.includes(lang)
        ? prev.filter((l) => l !== lang)
        : [...prev, lang]
    );
  };

  // ─── Single shared row-builder for EVERY translation download flow (AI
  // and Template), so the .xlsx format can never drift out of sync between
  // them. Always walks the CURRENT survey definition (so Field_Default is
  // populated even with zero saved translations - an empty-DB download is
  // still a ready-to-fill template, not an empty/error result), then
  // overlays `translationsMap` - the same {label: {translations: {qCode:
  // {title, rows, columns}}}} shape /get-translations returns - wherever
  // it has data. `blankIfMissing` controls what an untranslated cell shows:
  // false (AI download) falls back to the default text, matching the
  // original AI-download behavior; true (Template download) leaves it
  // blank, since blank is the honest signal "still needs translating."
  const buildTranslationRows = (languageLabels, translationsMap, blankIfMissing, dataOverride) => {
    let data = dataOverride;
    if (!data) {
      data = typeof sdataGroup === "string" ? JSON.parse(sdataGroup) : sdataGroup;
      if (!Array.isArray(data)) data = Object.values(data);
    }

    const cellFor = (parentType, questionCodeCol, type, code, qCodeForLookup, field, defaultText) => {
      const row = { "Parent Type": parentType, "Question Code": questionCodeCol, "Type": type, "Code": code, "Field_Default": defaultText };
      languageLabels.forEach((label) => {
        const qMap = translationsMap[label]?.translations?.[qCodeForLookup];
        let text = blankIfMissing ? "" : defaultText;
        if (qMap) {
          if (field === "title" && qMap.title) text = qMap.title;
          else if (field === "row" && qMap.rows?.[String(code)] != null) text = `${code}. ${qMap.rows[String(code)]}`;
          else if (field === "column" && qMap.columns?.[String(code)] != null) text = `${code}. ${qMap.columns[String(code)]}`;
        }
        row[`Field_${label}`] = text;
      });
      return row;
    };

    const rows = [];
    data.forEach((q) => {
      const qCode = q.Question_number;
      if (q.type === "Intro") {
        rows.push(cellFor("Question", "", `${qCode}Repeat`, "", qCode, "title", q.Qtitle || ""));
      } else {
        rows.push(cellFor("Question", qCode, "Text", "", qCode, "title", q.Qtitle || ""));
      }
      (q.rows || []).forEach((row) => {
        rows.push(cellFor("Question", qCode, "Rows", row.index, qCode, "row", `${row.index}. ${row.text}`));
      });
      (q.columns || []).forEach((col) => {
        rows.push(cellFor("Question", qCode, "Column", col.index, qCode, "column", `${col.index}. ${col.text}`));
      });
    });
    return rows;
  };

  // =========================================
  // HELPERS: Tag-aware text extraction & rebuilding
  // (in koi bhi in dono functions ke bahar, component ke top pe rakho)
  // =========================================
  const TAG_SPLIT_REGEX = /(<[^>]+>)/g;

  // String ko tag aur text segments me todta hai
  const splitIntoSegments = (str = "") => {
    if (!str) return [];
    return str
      .split(TAG_SPLIT_REGEX)
      .filter((part) => part !== "")
      .map((part) =>
        /^<[^>]+>$/.test(part)
          ? { type: "tag", content: part }
          : { type: "text", content: part }
      );
  };

  // Sirf non-empty, trimmed text segments nikaalta hai (translation ke liye)
  const getTranslatableUnits = (str = "") => {
    return splitIntoSegments(str)
      .filter((seg) => seg.type === "text" && seg.content.trim() !== "")
      .map((seg) => seg.content.trim());
  };

  // Segment ke original leading/trailing space preserve karke translated text daalta hai
  const rebuildTextSegment = (original, translatedTrimmed) => {
    const leading = original.match(/^\s*/)[0];
    const trailing = original.match(/\s*$/)[0];
    return `${leading}${translatedTrimmed}${trailing}`;
  };

  // Poore string ko translate karta hai, tags apni jagah pe rakhte hue
  // Returns { text, found } - found=false matlab is label ke liye koi translation nahi mila
  const translateWithTags = (str, label, unitMap) => {
    if (!str) return { text: str, found: false };
    let found = false;
    const rebuilt = splitIntoSegments(str)
      .map((seg) => {
        if (seg.type === "tag") return seg.content;
        const trimmed = seg.content.trim();
        if (!trimmed) return seg.content; // sirf whitespace wala segment
        const translations = unitMap[trimmed] || {};
        const translated = translations[label];
        if (translated) {
          found = true;
          return rebuildTextSegment(seg.content, translated);
        }
        return seg.content; // translation nahi mila -> original hi rakho
      })
      .join("");
    return { text: rebuilt, found };
  };

  // Adapts the AI /translate response (keyed by raw source text) into the
  // SAME translationsMap shape persisted (DB) translations use, purely
  // in-memory, so buildTranslationRows() above can serve both without
  // caring which one produced the data. Strips the "N. " prefix the AI
  // preserves in row/column text, since cellFor() re-adds it consistently.
  const buildTranslationsMapFromAiResult = (languageLabels, translatedData, dataOverride) => {
    const textToTranslations = {};
    translatedData.forEach((item) => {
      textToTranslations[item.original] = item.translations || {};
    });

    let data = dataOverride;
    if (!data) {
      data = typeof sdataGroup === "string" ? JSON.parse(sdataGroup) : sdataGroup;
      if (!Array.isArray(data)) data = Object.values(data);
    }

    const result = {};
    languageLabels.forEach((label) => {
      result[label] = { label, translations: {} };
    });

    data.forEach((q) => {
      const qCode = q.Question_number;
      // ---- Title ----
      if (q.Qtitle) {
        languageLabels.forEach((label) => {
          const { text, found } = translateWithTags(q.Qtitle, label, textToTranslations);
          if (!found) return;
          result[label].translations[qCode] = result[label].translations[qCode] || {};
          result[label].translations[qCode].title = text;
        });
      }

      // ---- Rows ----
      (q.rows || []).forEach((row) => {
        languageLabels.forEach((label) => {
          const { text, found } = translateWithTags(row.text, label, textToTranslations);
          if (!found) return;
          const entry = (result[label].translations[qCode] = result[label].translations[qCode] || {});
          entry.rows = entry.rows || {};
          entry.rows[String(row.index)] = text;
        });
      });

      // ---- Columns ----
      (q.columns || []).forEach((col) => {
        languageLabels.forEach((label) => {
          const { text, found } = translateWithTags(col.text, label, textToTranslations);
          if (!found) return;
          const entry = (result[label].translations[qCode] = result[label].translations[qCode] || {});
          entry.columns = entry.columns || {};
          entry.columns[String(col.index)] = text;
        });
      });
    });

    return result;
  };

  const downloadexcelfile = async (selectedLanguages) => {
    setIsDownloadingExcel(true);
    try {
      let data =
        typeof sdataGroup === "string" ? JSON.parse(sdataGroup) : sdataGroup;
      if (!Array.isArray(data)) {
        data = Object.values(data);
      }
      // console.log("FINAL DATA:", data);
      // =========================================
      // STEP 0: REMOVE QUESTIONS WHERE isTranslation === true
      // Ye poori Excel se exclude honge - na text bhejenge API ko,
      // na translation map me aayenge, na hi row banegi
      // =========================================
      const filteredData = data.filter(
        (q) => q.oeparameters?.isNoTranslation !== true
      );

      // =========================================
      // STEP 1: EXTRACT DISTINCT, TAG-FREE TEXT UNITS
      // (index prefix aur HTML/CSS bilkul nahi jaayenge API ko)
      // =========================================
      const uniqueTextsSet = new Set();

      const registerUnits = (str) => {
        getTranslatableUnits(str).forEach((unit) => uniqueTextsSet.add(unit));
      };

      filteredData.forEach((q) => {
        if (q.Qtitle) registerUnits(q.Qtitle);
        q.rows?.forEach((row) => registerUnits(row.text)); // sirf row.text, index prefix nahi
        q.columns?.forEach((col) => registerUnits(col.text));
      });

      const baseTexts = Array.from(uniqueTextsSet);
      // console.log("UNIQUE TEXTS SENT TO API:", baseTexts.length);

      // =========================================
      // STEP 2: TRANSLATION API
      // =========================================
      const response = await axios.post(
        `${config.API_BASE_URL}/translate`,
        {
          texts: baseTexts,
          languages: selectedLanguages,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const translatedData = response.data;
      console.log("TRANSLATED:", translatedData);

      // =========================================
      // STEP 3-6: shared row builder (same format as the Template
      // download and Upload parsing - see buildTranslationRows above)
      // =========================================
      const headers = [
        "Parent Type",
        "Question Code",
        "Type",
        "Code",
        "Field_Default",
        ...selectedLanguages.map((lang) => `Field_${lang}`),
      ];
      const aiTranslationsMap = buildTranslationsMapFromAiResult(selectedLanguages, translatedData, filteredData);
      const excelRows = buildTranslationRows(selectedLanguages, aiTranslationsMap, false, filteredData);
      console.log("EXCEL ROWS:", excelRows);

      // =========================================
      // STEP 7: CREATE EXCEL
      // =========================================
      const worksheet = XLSX.utils.json_to_sheet(excelRows, {
        header: headers,
        skipHeader: false,
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Survey");
      XLSX.writeFile(workbook, "Survey.xlsx");
    } catch (err) {
      console.error("Error:", err);
      showToastMessage("Failed to generate Excel file");
    } finally {
      setIsDownloadingExcel(false);
    }
  };


  const aiFilteredLanguages = languages.filter((lang) =>
    lang.label.toLowerCase().includes(aiLanguageSearch.toLowerCase())
  );
  const manageFilteredLanguages = languages.filter((lang) =>
    lang.label.toLowerCase().includes(manageLanguageSearch.toLowerCase())
  );

  // ─── Save the list of languages this survey supports (config only - like
  // redirects; does NOT touch any translated text). This drives which
  // languages the respondent-facing switcher offers. ───
  const saveSupportedLanguages = async (selectedLangLabels) => {
    setIsSavingLanguages(true);
    try {
      const res = await axios.post(`${config.API_BASE_URL}/save-language-config`, {
        projectId,
        languages: selectedLangLabels,
      });
      if (res.data?.success) {
        setSupportedLanguages(res.data.languages || selectedLangLabels);
        showToastMessage(`Saved ${selectedLangLabels.length} supported language(s)`);
      } else {
        showToastMessage("Failed to save supported languages");
      }
    } catch (err) {
      console.error("Error saving supported languages:", err);
      showToastMessage("Failed to save supported languages");
    } finally {
      setIsSavingLanguages(false);
    }
  };

  // Strips the "N. " index prefix the Excel export bakes into row/column
  // cell text (see downloadexcelfile's createLanguageObject) - mirrors the
  // backend's _strip_index_prefix() so uploaded/AI-generated translations
  // are stored the same clean way either way.
  const stripIndexPrefix = (index, text) => {
    const prefix = `${index}. `;
    return typeof text === "string" && text.startsWith(prefix) ? text.slice(prefix.length) : text;
  };

  // ─── Upload a corrected/hand-translated file (same column shape the
  // Download button produces) - manual-edit path, no AI involved ───
  const handleUploadTranslationFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingTranslations(true);
    try {
      const buf = await file.arrayBuffer();
      const workbook = XLSX.read(buf, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (!rows.length) {
        showToastMessage("The uploaded file has no rows");
        return;
      }

      const fieldCols = Object.keys(rows[0]).filter(
        (k) => k.startsWith("Field_") && k !== "Field_Default"
      );
      if (!fieldCols.length) {
        showToastMessage("No Field_<Language> columns found in this file");
        return;
      }

      // Field_<Label> columns map straight onto the Translation collection's
      // Language field - no code lookup needed, the label IS the identifier.
      const labelsFound = fieldCols.map((col) => ({ col, label: col.replace("Field_", "") }));
      const allWarnings = [];

      for (const { col, label } of labelsFound) {
        const translations = {};
        for (const row of rows) {
          const qCode = row["Question Code"];
          const type = row["Type"];
          const cellCode = row["Code"];
          const text = row[col];
          if (!qCode || text === undefined || text === "") continue;

          if (!translations[qCode]) translations[qCode] = {};
          if (type === "Text") {
            translations[qCode].title = text;
          } else if (type === "Rows") {
            translations[qCode].rows = translations[qCode].rows || {};
            translations[qCode].rows[String(cellCode)] = stripIndexPrefix(cellCode, text);
          } else if (type === "Column") {
            translations[qCode].columns = translations[qCode].columns || {};
            translations[qCode].columns[String(cellCode)] = stripIndexPrefix(cellCode, text);
          }
        }

        // Server-side validates each language's rows against the CURRENT
        // survey (see /save-translations -> _validate_translations_against_survey)
        // before persisting anything - an unknown question code rejects
        // with a 400, which axios surfaces as a thrown error, not a
        // resolved response, so it's caught here explicitly.
        try {
          const saveRes = await axios.post(`${config.API_BASE_URL}/save-translations`, {
            projectId,
            language: label,
            translations,
          });
          if (saveRes.data?.warnings?.length) {
            allWarnings.push(...saveRes.data.warnings.map((w) => `${label}: ${w}`));
          }
        } catch (saveErr) {
          const serverData = saveErr.response?.data;
          const details = (serverData?.details || []).join("; ");
          showToastMessage(
            `Upload rejected for ${label}: ${serverData?.error || "validation failed"}${details ? ` (${details})` : ""}`
          );
          return; // stop here - this file doesn't match the current survey, don't attempt the rest
        }
      }

      const res = await fetch(`${config.API_BASE_URL}/get-translations/${projectId}`, {
        credentials: "include",
      });
      const result = await res.json();
      if (result.success) setTranslationsByLang(result.translations);

      if (allWarnings.length) {
        console.warn("Translation upload warnings (stale row/column entries skipped):", allWarnings);
        showToastMessage(`Imported ${labelsFound.length} language(s) - ${allWarnings.length} stale entr${allWarnings.length === 1 ? "y" : "ies"} skipped (see console)`);
      } else {
        showToastMessage(`Imported ${labelsFound.length} language(s) from file`);
      }
    } catch (err) {
      console.error("Error uploading translations:", err);
      showToastMessage("Failed to import translations file - check the file matches the exported format");
    } finally {
      setIsUploadingTranslations(false);
      if (translationUploadInputRef.current) translationUploadInputRef.current.value = "";
    }
  };

  // ─── Download what's actually PERSISTED in MongoDB (not a fresh AI call) -
  // same column shape as the AI download, for review/audit/re-edit-and-reupload ───
  // Manual template download - ALWAYS produces a usable file, even with
  // zero saved translations: Field_Default is populated straight from the
  // current survey content (via the shared buildTranslationRows), and
  // Field_<Language> is blank wherever nothing's saved yet, ready to fill
  // in and re-upload. Language columns come from the survey's configured
  // supported languages first; if none are configured yet, falls back to
  // whatever's checked in the picker so this is never a dead end.
  const downloadTranslationTemplate = () => {
    try {
      // Never a dead click: with no languages selected/configured yet,
      // still produce a structure-only template (Field_Default columns
      // only) instead of silently doing nothing - matches "a template
      // must download even when no translations exist yet".
      const labels = supportedLanguages.length ? supportedLanguages : manageSelectedLanguages;

      const headers = ["Parent Type", "Question Code", "Type", "Code", "Field_Default",
        ...labels.map((label) => `Field_${label}`)];
      const excelRows = buildTranslationRows(labels, translationsByLang, true);

      if (!excelRows.length) {
        showToastMessage("This survey has no translatable questions yet");
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(excelRows, { header: headers, skipHeader: false });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Translations");
      XLSX.writeFile(workbook, "Survey_TranslationTemplate.xlsx");

      if (!labels.length) {
        showToastMessage("Downloaded with no language columns - pick languages and Save as Supported to include them next time");
      }
    } catch (err) {
      console.error("Error downloading translation template:", err);
      showToastMessage("Failed to build the translation template");
    }
  };

  const handleSaveSurvey = async () => {
    if (!projectId) {
      showToastMessage("No project ID available");
      return;
    }

    const issues = validateSurvey(sdataGroup);
    if (issues.length > 0) {
      showToastMessage(`Cannot save: ${issues.length} error(s) found. Please fix them first.`);
      return; // block save
    }

    try {
      setIsSaving(true);
      setProcessingMessage("Saving survey...");

      const normalizedData = normalizeRequiredFlags(sdataGroup);
      setSdataGroup(normalizedData);

      const response = await fetch(`${config.API_BASE_URL}/save-survey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          projectId,
          sdataGroup: normalizedData,
          client: JSON.stringify(selectedClient),
          project: JSON.stringify(selectedProject),
        }),
      });
      if (!response.ok) throw new Error("Failed to save survey");
      await response.json();
      showToastMessage("Survey saved successfully!");
      setHasUnsavedChanges(false);
      if (onSurveyUpdate) onSurveyUpdate(normalizedData);
      // Persist the change log alongside the survey - previously only the
      // Builder toolbar's Save did this, so entries created while working in
      // the Preview (incl. applied AI changes) were lost on refresh if the
      // user saved from here.
      if (onPersistChangeLog) {
        try {
          await onPersistChangeLog();
        } catch (e) {
          console.error("Failed to persist change log:", e);
        }
      }
      setProcessingMessage("");
    } catch (error) {
      console.error("Error saving survey:", error);
      showToastMessage("Failed to save survey");
    } finally {
      setIsSaving(false);
      setProcessingMessage("");
    }
  };

  const handlePreviewChange = (value) => {
    if (value === "translation") {
      setManageSelectedLanguages(supportedLanguages); // pre-check what's already configured, like RedirectModal does
      setAiSelectedLanguages([]); // AI section always starts unselected
      setManageLanguageSearch("");
      setAiLanguageSearch("");
      setSelectedExport(null);
      setPreviewType(value);
      return;
    }
    if (value.startsWith("export_")) {
      setSelectedExport(value.replace("export_", ""));
      setPreviewType("export");
    } else {
      setSelectedExport(null);
      setPreviewType(value);
    }
  };

  const toggleSplitView = () => {
    if (!questionnaireFileId) {
      showToastMessage("No document file available for this survey");
      return;
    }
    setShowSplitView(!showSplitView);
  };

  // ─── DOCX divider drag ───────────────────────────────────────────────────────
  const handleMouseDown = (e) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newRatio =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;
      setSplitRatio(Math.max(20, Math.min(80, newRatio)));
    },
    [isDragging]
  );

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove]);

  // ─── Voice assistant ─────────────────────────────────────────────────────────
  const speak = (text) => {
    if ("speechSynthesis" in window && !isSpeaking) {
      speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.2;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      speechSynthesis.speak(utterance);
    }
  };

  const handleVoiceCommand = (command) => {
    if (isSpeaking) return;
    let updated = false;
    const qMatch =
      command.match(/question (\d+(?:\.\d+)?)/i) ||
      command.match(/q(\d+(?:\.\d+)?)/i);
    const questionNum = qMatch ? parseInt(qMatch[1]) : null;
    const questionKey = questionNum
      ? questionNum.toString()
      : Object.keys(sdataGroup)[0];

    if (!sdataGroup || !sdataGroup[questionKey]) {
      speak("Question not found.");
      return;
    }

    const isBold = /bold|emphasize|strong/i.test(command);
    const isUnbold = /unbold|remove bold|normal/i.test(command);
    const targetTitle = /title|question text/i.test(command);
    const targetRow = command.match(/(?:row|option)s?\s*(\d+(?:\.\d+)?)/i);
    const targetCol = command.match(/(?:column|col)s?\s*(\d+(?:\.\d+)?)/i);
    const targetStmt = command.match(/(?:statement|item)s?\s*(\d+(?:\.\d+)?)/i);

    let target = null;
    let index = null;
    let subKey = null;

    if (targetTitle) {
      target = "Qtitle";
    } else if (targetRow) {
      const rowNum = parseInt(targetRow[1]) - 1;
      if (
        sdataGroup[questionKey]?.rows &&
        rowNum >= 0 &&
        rowNum < sdataGroup[questionKey].rows.length
      ) {
        target = "rows";
        index = rowNum;
        subKey = "text";
      } else {
        speak(`Row ${targetRow[1]} not found.`);
        return;
      }
    } else if (targetCol) {
      const colNum = parseInt(targetCol[1]) - 1;
      if (
        sdataGroup[questionKey]?.columns &&
        colNum >= 0 &&
        colNum < sdataGroup[questionKey].columns.length
      ) {
        target = "columns";
        index = colNum;
        subKey = "text";
      } else {
        speak(`Column ${targetCol[1]} not found.`);
        return;
      }
    } else if (targetStmt) {
      const stmtNum = parseInt(targetStmt[1]) - 1;
      if (
        sdataGroup[questionKey]?.statements &&
        stmtNum >= 0 &&
        stmtNum < sdataGroup[questionKey].statements.length
      ) {
        target = "statements";
        index = stmtNum;
        subKey = "text";
      } else {
        speak(`Statement ${targetStmt[1]} not found.`);
        return;
      }
    }

    if (target && (isBold || isUnbold)) {
      if (isBold) {
        updated = updateTextBold(questionKey, target, index, subKey);
        speak(
          `${target === "Qtitle" ? "title" : `${target} ${index + 1}`} bolded.`
        );
      } else if (isUnbold) {
        updated = updateTextUnbold(questionKey, target, index, subKey);
        speak(
          `${target === "Qtitle" ? "title" : `${target} ${index + 1}`
          } unbolded.`
        );
      }
    }

    if (!updated && command.length > 5 && !/stop|exit|cancel/i.test(command)) {
      setTimeout(() => {
        speak("Command not recognized. Try 'bold title' or 'unbold row two'.");
      }, 300);
    }

    if (updated) setSdataGroup((prev) => ({ ...prev }));
  };

  const updateTextBold = (questionKey, target, index = null, subKey = null) => {
    const toggleBold = (text) => {
      if (typeof text !== "string") return text;
      const boldRegex = /^<strong>\s*(.*?)\s*<\/strong>$/i;
      return boldRegex.test(text)
        ? text.replace(boldRegex, "$1").trim()
        : `<strong>${text}</strong>`;
    };
    setSdataGroup((prev) => {
      if (!prev || !prev[questionKey]) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const q = newData[questionKey];
      if (target === "Qtitle") {
        q.Qtitle = toggleBold(q.Qtitle || "");
      } else if (index !== null && q[target]?.[index] && subKey) {
        q[target][index][subKey] = toggleBold(q[target][index][subKey]);
      }
      return newData;
    });
    return true;
  };

  const updateTextUnbold = (
    questionKey,
    target,
    index = null,
    subKey = null
  ) => {
    const stripBold = (text) =>
      typeof text !== "string"
        ? text
        : text.replace(/<strong>\s*(.*?)\s*<\/strong>/gi, "$1");
    setSdataGroup((prev) => {
      if (!prev || !prev[questionKey]) return prev;
      const newData = JSON.parse(JSON.stringify(prev));
      const q = newData[questionKey];
      if (target === "Qtitle") {
        q.Qtitle = stripBold(q.Qtitle || "");
      } else if (index !== null && q[target]?.[index] && subKey) {
        q[target][index][subKey] = stripBold(q[target][index][subKey]);
      }
      return newData;
    });
    return true;
  };

  const toggleListening = () => {
    if (!recognitionRef.current) {
      speak("Voice not supported.");
      return;
    }
    speechSynthesis.cancel();
    setIsSpeaking(false);
    if (isListening) {
      recognitionRef.current.stop();
      setTranscript("");
    } else {
      setTranscript("Ready...");
      recognitionRef.current.start();
    }
    setIsListening(!isListening);
    if (!isSpeaking) speak(isListening ? "Stopped." : "Say your command now.");
  };

  // ─── Row / column setting badges ──────────────────────────────────────────────
  const getRowSettingLabels = (row) => {
    const labels = [];
    if (row.screenout)
      labels.push({ text: "Screen Out", color: "#dc2626", bgColor: "#fecaca" });
    if (row.other)
      labels.push({ text: "Other", color: "#7c3aed", bgColor: "#ddd6fe" });
    if (row.otherPiping)
      labels.push({
        text: `Piping ${row.otherPiping}`,
        color: "#0369a1",
        bgColor: "#bae6fd",
      });
    if (row.filterLogic)
      labels.push({
        text: `Logic: ${row.filterLogic}`,
        color: "#ea580c",
        bgColor: "#fed7aa",
      });
    if (row.randomize)
      labels.push({
        text: "Randomize",
        color: "#059669",
        bgColor: "#a7f3d0",
      });
    if (row.exclusive)
      labels.push({
        text: "Exclusive",
        color: "#d97706",
        bgColor: "#fde68a",
      });
    if (row.hidden)
      labels.push({ text: "Hidden", color: "#4b5563", bgColor: "#d1d5db" });
    if (row.anchor)
      labels.push({ text: "Anchor", color: "#65a30d", bgColor: "#d9f99d" });
    if (row.rotationGroup)
      labels.push({
        text: `Rotation: ${row.rotationGroup}`,
        color: "#be185d",
        bgColor: "#fbcfe8",
      });
    if (row.fix)
      labels.push({ text: "Fix", color: "#4338ca", bgColor: "#c7d2fe" });
    if (row.header)
      labels.push({ text: "Header", color: "#0f766e", bgColor: "#99f6e4" });
    if (row.settings) {
      Object.entries(row.settings).forEach(([key, value]) => {
        if (
          value !== false &&
          value !== null &&
          value !== undefined &&
          value !== ""
        ) {
          labels.push({
            text: `${key}: ${value}`,
            color: "#6b7280",
            bgColor: "#e5e7eb",
          });
        }
      });
    }
    return labels;
  };

  const badgeStyle = (bg, color) => ({
    fontSize: "11px",
    padding: "3px 10px",
    backgroundColor: bg,
    color,
    borderRadius: "4px",
    fontWeight: 600,
    display: "inline-block",
  });

  const renderRowBadges = (row) => {
    if (!row) return null;
    const badges = [];
    if (row.screenout)
      badges.push(
        <span key="so" style={badgeStyle("#fee2e2", "#991b1b")}>
          Screen Out
        </span>
      );
    if (row.exclusive)
      badges.push(
        <span key="ex" style={badgeStyle("#dbeafe", "#1e40af")}>
          Exclusive
        </span>
      );
    if (row.other)
      badges.push(
        <span key="ot" style={badgeStyle("#fef3c7", "#92400e")}>
          Other (specify)
        </span>
      );
    if (row.fix)
      badges.push(
        <span key="fx" style={badgeStyle("#e0e7ff", "#3730a3")}>
          Fixed
        </span>
      );
    if (row.quotaCounts)
      badges.push(
        <span key="qt" style={badgeStyle("#f3e8ff", "#6b21a8")}>
          Quota: {row.quotaCounts}
        </span>
      );
    if (row.punchLogic)
      badges.push(
        <span key="pl" style={badgeStyle("#ecfdf5", "#065f46")}>
          Punch: {row.punchLogic}
        </span>
      );
    if (row.filterLogic)
      badges.push(
        <span key="fl" style={badgeStyle("#f3e8ff", "#6b21a8")}>
          Filter: {row.filterLogic}
        </span>
      );
    return badges.length > 0 ? (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          marginTop: "6px",
        }}
      >
        {badges}
      </div>
    ) : null;
  };

  // ─── Respondent (survey) view ─────────────────────────────────────────────────
  const renderSurveyView = (q) => {
    if (!q)
      return (
        <div style={{ color: "#6b7280", padding: "2rem", textAlign: "center" }}>
          No question data available
        </div>
      );

    const titleHtml = { __html: q.Qtitle || "No question text" };
    const type = q.type || "Unknown";

    if (type === "Intro") {
      return (
        <div style={{ textAlign: "center", padding: "3rem 1rem" }}>
          <div
            style={{ fontSize: "20px", marginBottom: "2rem", color: "#1f2937" }}
            dangerouslySetInnerHTML={titleHtml}
          />
          <button
            style={{
              padding: "14px 48px",
              background: "#2563eb",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "15px",
              fontWeight: 600,
              cursor: "default",
              transition: "background-color 0.2s"
            }}
          >
            CONTINUE
          </button>
        </div>
      );
    }

    if (type === "Single Response" && q.rows?.length > 0) {
      return <SingleResponsePreview question={q} />;
    }

    if (type === "Numeric") {
      const oe = q.oeparameters || {};
      return (
        <div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: 600,
              marginBottom: "1.5rem",
              color: "#1f2937"
            }}
            dangerouslySetInnerHTML={titleHtml}
          />
          <input
            type="number"
            min={oe.min}
            max={oe.max}
            placeholder="Enter your answer"
            readOnly
            style={{
              width: "200px",
              padding: "12px 16px",
              fontSize: "16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              backgroundColor: "#f9fafb",
              color: "#6b7280"
            }}
          />
        </div>
      );
    }

    if (q.rows?.length > 0 && q.columns?.length > 0) {
      const isMulti = type.includes("Multi") || type === "Multi Grid";
      return (
        <div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: 600,
              marginBottom: "1.5rem",
              color: "#1f2937"
            }}
            dangerouslySetInnerHTML={titleHtml}
          />
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "800px",
                fontSize: "14px"
              }}
            >
              <thead>
                <tr style={{ background: "#f1f5f9" }}>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "left",
                      border: "1px solid #e2e8f0",
                      minWidth: "300px",
                      fontWeight: 600,
                      fontSize: "13px",
                      color: "#334155"
                    }}
                  >
                    Statement / Item
                  </th>
                  {q.columns.map((col) => (
                    <th
                      key={col.index}
                      style={{
                        padding: "14px 16px",
                        textAlign: "center",
                        border: "1px solid #e2e8f0",
                        minWidth: "120px",
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#334155",
                        whiteSpace: "normal",
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{ __html: col.text || "—" }}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.rows.map((row, i) => (
                  <tr
                    key={row.index || i}
                    style={{
                      background: i % 2 === 0 ? "#ffffff" : "#f8fafc",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px 16px",
                        border: "1px solid #e2e8f0",
                        fontSize: "14px",
                      }}
                    >
                      <div
                        style={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          marginBottom: "6px",
                        }}
                        dangerouslySetInnerHTML={{ __html: row.text || "—" }}
                      />
                      {renderRowBadges(row)}
                    </td>
                    {q.columns.map((col) => (
                      <td
                        key={col.index}
                        style={{
                          textAlign: "center",
                          padding: "12px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <input
                          type={isMulti ? "checkbox" : "radio"}
                          readOnly
                          style={{ width: "18px", height: "18px", cursor: "default" }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div style={{ color: "#6b7280", padding: "2rem 0" }}>
        <div
          style={{ fontSize: "20px", color: "#1f2937" }}
          dangerouslySetInnerHTML={titleHtml}
        />
        <p style={{ marginTop: "1rem", fontSize: "14px" }}>
          (No preview available for type: {type})
        </p>
      </div>
    );
  };

  // ─── Export preview ───────────────────────────────────────────────────────────
  const renderExportPreview = () => {
    if (!selectedExport || !exportedFiles[selectedExport]) {
      return (
        <div className="preview-empty">
          <FaFileAlt size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
          <h4>No export selected</h4>
          <p>Select an export from the dropdown above to view its content</p>
        </div>
      );
    }
    const exportData = exportedFiles[selectedExport];
    return (
      <div className="export-preview-container">
        <div className="export-preview-header">
          <div>
            <h4>{exportData.fileName}</h4>
            <p>Exported: {new Date(exportData.timestamp).toLocaleString()}</p>
          </div>
          <button
            className="action-button primary"
            onClick={() => {
              const blob = new Blob([exportData.content], {
                type: "text/plain",
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = exportData.fileName;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            <FaDownload /> Download
          </button>
        </div>
        <div className="export-preview-body">
          <pre>{exportData.content}</pre>
        </div>
      </div>
    );
  };

  // ─── Translation management preview (inline, like every other preview type) ───
  const renderTranslationPreview = () => {
    if (isDownloadingExcel || isUploadingTranslations || isSavingLanguages) {
      return (
        <div className="translation-preview-container">
          <div className="lang-popup-loading">
            <span className="lang-popup-spinner" />
            <p className="lang-popup-loading-title">
              {isSavingLanguages
                ? "Saving supported languages…"
                : isUploadingTranslations
                  ? "Importing translations from file…"
                  : "Translating & preparing your Excel file..."}
            </p>
            <p className="lang-popup-loading-hint">
              This can take a moment for multiple languages
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="translation-preview-container">


        {/* ───── AI Translation: quick, ephemeral AI-drafted Excel - never
            touches the database on its own, only what you later upload via
            Manage Translation below. Its own picker, always starts with
            NOTHING checked - this is a one-off "translate these right now"
            pick, not a reflection of what's already configured. ───── */}
        <div className="lang-popup-section">
          <h4 className="lang-popup-section-title">AI Translation</h4>
          <p className="lang-popup-step-hint">
            Pick languages and get an AI-translated Excel draft. Nothing is
            saved to the database until you upload a (possibly corrected)
            copy back in Manage Translation below.
          </p>

          <div className="lang-popup-count">
            {aiSelectedLanguages.length} selected
          </div>
          <input
            type="text"
            value={aiLanguageSearch}
            onChange={(e) => setAiLanguageSearch(e.target.value)}
            placeholder="Search languages..."
            className="lang-popup-search"
          />
          <div className="lang-popup-list">
            {aiFilteredLanguages.length === 0 ? (
              <div className="lang-popup-empty">No languages found</div>
            ) : (
              aiFilteredLanguages.map((lang) => (
                <label key={lang.code} className="lang-popup-item">
                  <input
                    type="checkbox"
                    checked={aiSelectedLanguages.includes(lang.label)}
                    onChange={() => handleAiLanguageChange(lang.label)}
                  />
                  {lang.label}
                </label>
              ))
            )}
          </div>

          <div className="lang-popup-actions">
            <button
              onClick={() => downloadexcelfile(aiSelectedLanguages)}
              disabled={aiSelectedLanguages.length === 0}
              className="lang-popup-btn download"
              title="AI-translate and download an editable Excel file - not saved to the database"
            >
              <FaDownload /> Download (AI)
            </button>
          </div>
        </div>

        {/* ───── Manage Translation: what respondents actually see in the
            live PollStudioNST survey - supported-languages config + the
            persisted (DB) translation text. Its own picker, pre-checked
            from whatever's already saved as supported. ───── */}

        <div className="lang-popup-section">
          <h4 className="lang-popup-section-title">Manage Translation</h4>
          <p className="lang-popup-step-hint">
            1. <strong>Save as Supported</strong> so respondents can pick
            these languages in PollStudioNST. 2. <strong>Download
              Template</strong> - current survey text plus whatever's already
            saved (blank where nothing's translated yet). 3. Fill it in and
            {" "}<strong>Upload</strong> to persist translations - same file
            format as the AI download above.
          </p>
          {supportedLanguages.length > 0 && (
            <div className="lang-popup-existing">
              <span className="lang-popup-existing-label">Supported languages:</span>
              {supportedLanguages.map((label) => (
                <span key={label} className="lang-popup-existing-chip">{label}</span>
              ))}
            </div>
          )}
          {Object.keys(translationsByLang).length > 0 && (
            <div className="lang-popup-existing">
              <span className="lang-popup-existing-label">Translations saved in DB:</span>
              {Object.keys(translationsByLang).map((label) => (
                <span key={label} className="lang-popup-existing-chip saved">{label}</span>
              ))}
            </div>
          )}

          <div className="lang-popup-count">
            {manageSelectedLanguages.length} selected
          </div>
          <input
            type="text"
            value={manageLanguageSearch}
            onChange={(e) => setManageLanguageSearch(e.target.value)}
            placeholder="Search languages..."
            className="lang-popup-search"
          />
          <div className="lang-popup-list">
            {manageFilteredLanguages.length === 0 ? (
              <div className="lang-popup-empty">No languages found</div>
            ) : (
              manageFilteredLanguages.map((lang) => (
                <label key={lang.code} className="lang-popup-item">
                  <input
                    type="checkbox"
                    checked={manageSelectedLanguages.includes(lang.label)}
                    onChange={() => handleManageLanguageChange(lang.label)}
                  />
                  {lang.label}
                </label>
              ))
            )}
          </div>

          <div className="lang-popup-actions">
            <button
              onClick={() => saveSupportedLanguages(manageSelectedLanguages)}
              className="lang-popup-btn supported"
              title="Save this list as the languages respondents can choose (config only, no text)"
            >
              <FaCheck /> Save as Supported
            </button>
            <button
              onClick={downloadTranslationTemplate}
              className="lang-popup-btn download-saved"
              title="Download a fill-in template: current survey text, plus any translations already saved - ready to complete and re-upload"
            >
              <FaDownload /> Download Template
            </button>
            <button
              onClick={() => translationUploadInputRef.current?.click()}
              className="lang-popup-btn upload"
              title="Upload a corrected/hand-translated file to store it in the database"
            >
              <FaFileAlt /> Upload File
            </button>
            <input
              ref={translationUploadInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleUploadTranslationFile}
            />
          </div>
        </div>
      </div>
    );
  };

  // ─── JSON preview ─────────────────────────────────────────────────────────────
  const renderJSONPreview = () => (
    <div className="preview-json">
      <div className="json-editor-header">
        <span className="hint">
          <FaEdit style={{ marginRight: "6px" }} />
          Edit the survey JSON directly — changes apply live. Use Undo / Redo
          (Ctrl+Z / Ctrl+Y) to step through your edits.
        </span>
        <div className="status">
          {jsonError ? (
            <span className="invalid">
              ⚠ Invalid JSON — changes not applied
            </span>
          ) : (
            <span className="valid">
              <FaCheckCircle style={{ marginRight: "4px" }} />
              Valid JSON
            </span>
          )}
          <button
            type="button"
            onClick={handleCopyJson}
            className={`json-copy-btn ${jsonCopied ? "copied" : ""}`}
          >
            {jsonCopied ? "✓ Copied" : "⧉ Copy JSON"}
          </button>
        </div>
      </div>
      <textarea
        className={`json-textarea ${jsonError ? "error" : ""}`}
        value={jsonText}
        onChange={(e) => handleJsonEdit(e.target.value)}
        spellCheck={false}
        wrap="soft"
      />
    </div>
  );

  // ─── Statistics preview ───────────────────────────────────────────────────────
  const renderStatisticsPreview = () => {
    if (!sdataGroup) return <div className="preview-empty">No data available</div>;

    const totalQuestions = Object.keys(sdataGroup).length;
    const questionTypes = {};
    let totalRows = 0;
    let totalColumns = 0;
    let totalStatements = 0;
    let rowsWithSettings = 0;
    let columnsWithSettings = 0;

    Object.values(sdataGroup).forEach((question) => {
      questionTypes[question.type] = (questionTypes[question.type] || 0) + 1;
      totalRows += (question.rows || []).length;
      totalColumns += (question.columns || []).length;
      totalStatements += (question.statements || []).length;
      if (question.rows)
        question.rows.forEach((row) => {
          if (getRowSettingLabels(row).length > 0) rowsWithSettings++;
        });
      if (question.columns)
        question.columns.forEach((col) => {
          if (getRowSettingLabels(col).length > 0) columnsWithSettings++;
        });
    });

    return (
      <div className="preview-stats">
        <h3><FaChartBar style={{ marginRight: "10px" }} />Survey Statistics</h3>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value blue">{totalQuestions}</div>
            <div className="stat-label">Total Questions</div>
          </div>
          <div className="stat-card">
            <div className="stat-value blue">{totalRows}</div>
            <div className="stat-label">Total Rows</div>
          </div>
          <div className="stat-card">
            <div className="stat-value blue">{totalColumns}</div>
            <div className="stat-label">Total Columns</div>
          </div>
          <div className="stat-card">
            <div className="stat-value blue">{totalStatements}</div>
            <div className="stat-label">Total Statements</div>
          </div>
          <div className="stat-card">
            <div className="stat-value purple">{rowsWithSettings}</div>
            <div className="stat-label">Rows with Settings</div>
          </div>
          <div className="stat-card">
            <div className="stat-value purple">{columnsWithSettings}</div>
            <div className="stat-label">Columns with Settings</div>
          </div>
        </div>

        <div className="question-types-section">
          <h4><FaList style={{ marginRight: "8px" }} />Question Types Distribution</h4>
          <div className="question-types-grid">
            {Object.entries(questionTypes)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => (
                <div key={type} className="type-item">
                  <span className="type-name">{type}</span>
                  <span className="type-count">{count}</span>
                </div>
              ))}
          </div>
          {Object.keys(questionTypes).length === 0 && (
            <p className="no-questions">No question types found</p>
          )}
        </div>
      </div>
    );
  };

  // Most oe params are only worth displaying when truthy/non-empty
  const isDisplayableOeParam = ([key, value]) => {
    if (key === "isRequired") {
      return typeof value === "boolean";
    }
    return value !== false && value !== null && value !== undefined && value !== "";
  };

  // ─── Questions preview (with comment boxes) ───────────────────────────────────
  const renderQuestionsPreview = () => {
    if (!sdataGroup || Object.keys(sdataGroup).length === 0) {
      return <div className="preview-empty">
        <FaClipboardList size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
        <h4>No questions available</h4>
        <p>Add questions to your survey to preview them here</p>
      </div>;
    }

    const sortedEntries = Object.entries(sdataGroup).sort((a, b) => {
      return (a[1]._order || 0) - (b[1]._order || 0);
    });

    const hasAnyComment =
      Object.values(comments).some((c) => c?.trim()) || masterComment.trim().length > 0;

    return (
      <div>
        {sortedEntries.map(([questionNumber, question]) => (
          <div key={questionNumber} className="preview-question">
            <div className="preview-question-header">
              <span className="preview-qnumber">
                <FaQuestionCircle style={{ marginRight: "6px", color: "#2563eb" }} />
                {question.Question_number || questionNumber}
              </span>
              <span className="preview-qtype">{question.type}</span>
            </div>

            <div
              className="preview-qtitle"
              dangerouslySetInnerHTML={{ __html: question.Qtitle || "No title" }}
            />

            {question.instructions && (
              <div className="preview-instructions">
                <strong><FaInfoCircle style={{ marginRight: "4px" }} />Instructions:</strong>
                <span
                  dangerouslySetInnerHTML={{ __html: question.instructions }}
                />
              </div>
            )}

            {question.logics && (
              <div className="preview-logic">
                <strong><FaExclamationTriangle style={{ marginRight: "4px" }} />Logic:</strong> {question.logics}
              </div>
            )}

            {question.rows && question.rows.length > 0 && (
              <div className="preview-rows">
                <strong><FaList style={{ marginRight: "4px" }} />Rows ({question.rows.length}):</strong>
                <ul>
                  {question.rows.map((row, index) => {
                    const settingLabels = getRowSettingLabels(row);
                    return (
                      <li key={index} className="preview-row-item">
                        <div className="row-content">
                          <span className="row-index">
                            {row.index || index + 1}
                          </span>
                          <div className="row-text-and-settings">
                            <span
                              className="row-text"
                              dangerouslySetInnerHTML={{ __html: row.text }}
                            />
                            {settingLabels.length > 0 && (
                              <div className="row-settings-inline">
                                {settingLabels.map((label, li) => (
                                  <span
                                    key={li}
                                    className="row-setting-inline"
                                    style={{
                                      color: label.color,
                                      backgroundColor: label.bgColor,
                                      borderColor: label.color,
                                    }}
                                  >
                                    {label.text}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {question.columns && question.columns.length > 0 && (
              <div className="preview-columns">
                <strong><FaThLarge style={{ marginRight: "4px" }} />Columns ({question.columns.length}):</strong>
                <div className="preview-columns-list">
                  {question.columns.map((col, index) => {
                    const settingLabels = getRowSettingLabels(col);
                    return (
                      <div key={index} className="preview-column-item">
                        <span className="column-index">
                          {col.index || index + 1}.
                        </span>
                        <div className="column-text-and-settings">
                          <span
                            className="column-text"
                            dangerouslySetInnerHTML={{ __html: col.text }}
                          />
                          {settingLabels.length > 0 && (
                            <div className="column-settings-inline">
                              {settingLabels.map((label, li) => (
                                <span
                                  key={li}
                                  className="column-setting-inline"
                                  style={{
                                    color: label.color,
                                    backgroundColor: label.bgColor,
                                    borderColor: label.color,
                                  }}
                                >
                                  {label.text}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {question.statements && question.statements.length > 0 && (
              <div className="preview-statements">
                <strong><FaClipboardList style={{ marginRight: "4px" }} />Statements ({question.statements.length}):</strong>
                <ul>
                  {question.statements.map((stmt, index) => (
                    <li
                      key={index}
                      dangerouslySetInnerHTML={{ __html: stmt.text }}
                    />
                  ))}
                </ul>
              </div>
            )}

            <div className="preview-settings">
              {question.rowsRandomisation && (
                <span className="setting-badge rows-random">Rows Random</span>
              )}
              {question.columnsRandomisation && (
                <span className="setting-badge cols-random">Cols Random</span>
              )}
              {question.rowsRotation && (
                <span className="setting-badge rows-rotate">Rows Rotate</span>
              )}
              {question.columnsRotation && (
                <span className="setting-badge cols-rotate">Cols Rotate</span>
              )}
              {question.alphabeticalOrder && (
                <span className="setting-badge az-order">A-Z Order</span>
              )}
              {question.columnIndex && (
                <span className="setting-badge col-index">Col Index</span>
              )}
            </div>

            {question.oeparameters &&
              Object.entries(question.oeparameters).filter(isDisplayableOeParam).length > 0 && (
                <div className="preview-section">
                  <label><FaCalculator style={{ marginRight: "4px" }} />Parameters:</label>
                  <div className="preview-oe-params">
                    {Object.entries(question.oeparameters)
                      .filter(isDisplayableOeParam)
                      .map(([key, value]) => (
                        <div key={key} className="oe-param-item">
                          <span className="param-key">{key}:</span>
                          <span className="param-value">{String(value)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

            {/* ── Per-question comment for AI ── */}
            <div className="comment-box">
              <label><FaComments style={{ marginRight: "4px" }} />Comment for AI:</label>
              <textarea
                className={comments[questionNumber]?.trim() ? "has-comment" : ""}
                value={comments[questionNumber] || ""}
                onChange={(e) =>
                  setComments((prev) => ({
                    ...prev,
                    [questionNumber]: e.target.value,
                  }))
                }
                placeholder="Describe what you'd like AI to change or review for this question..."
                rows={2}
              />
            </div>
          </div>
        ))}

        {/* ── Master comment box ── */}
        <div className={`master-comment-box ${masterComment.trim() ? "has-comment" : ""}`}>
          <div className="header">
            <span>🧠</span>
            <label>Master Instruction for AI (applies to entire survey)</label>
          </div>
          <textarea
            className={masterComment.trim() ? "has-comment" : ""}
            value={masterComment}
            onChange={(e) => setMasterComment(e.target.value)}
            placeholder={
              "Examples:\n" +
              "• Add hidden quota questions that are missing for Q3 and Q5\n" +
              "• Add a screening termination question before Q1\n" +
              "• All grid questions should have columnsRandomisation: true\n" +
              "• Create a HIDDEN derived variable after Q8 that codes age groups"
            }
            rows={5}
          />
          <p className="hint">
            This box sends ONE batch call to AI with a compact survey summary — token-efficient for survey-wide changes.
          </p>
        </div>

        {/* ── Submit to AI section ── */}
        <div className="ai-submit-section">
          {aiSubmitMessage && (
            <div className={`ai-submit-message ${aiSubmitMessage.success ? "success" : "error"}`}>
              {aiSubmitMessage.text}
            </div>
          )}

          <button
            className="ai-submit-btn"
            onClick={handleSubmitToAI}
            disabled={isSubmittingToAI || !hasAnyComment || aiSubmitAttempts >= 3}
            title={
              aiSubmitAttempts >= 3
                ? "Maximum 3 attempts reached — close and reopen the preview to reset"
                : !hasAnyComment
                  ? "Add a per-question comment or a master instruction before submitting"
                  : `Submit all comments to AI (attempt ${aiSubmitAttempts + 1} of 3)`
            }
          >
            {isSubmittingToAI
              ? <><FaRobot style={{ marginRight: "8px" }} />Submitting to AI…</>
              : aiSubmitAttempts >= 3
                ? "Max Attempts Reached"
                : <><FaRobot style={{ marginRight: "8px" }} />Submit to AI ({aiSubmitAttempts + 1}/3)</>}
          </button>

          {aiSubmitAttempts >= 3 ? (
            <p className="ai-submit-info error">
              Maximum 3 attempts reached. Close and reopen the preview to reset.
            </p>
          ) : !hasAnyComment ? (
            <p className="ai-submit-info">
              Add a per-question comment or fill the Master Instruction box to enable submission
            </p>
          ) : (
            <p className="ai-submit-info">
              {3 - aiSubmitAttempts} attempt{3 - aiSubmitAttempts !== 1 ? "s" : ""} remaining — send all your comments in one batch for best results
            </p>
          )}
        </div>

        {/* ── Upload latest QNR section ── */}
        <div className="ai-submit-section">
          <p className="hint">
            Have a revised questionnaire document? Upload it to see everything
            that's different from the current survey — content only
            (titles, options, question type). Settings like OE parameters,
            quotas and logic are never touched.
          </p>
          <input
            ref={qnrUploadInputRef}
            type="file"
            accept=".docx,.doc"
            onChange={handleUploadQnrFile}
            style={{ display: "none" }}
          />
          <button
            className="ai-submit-btn"
            onClick={() => qnrUploadInputRef.current?.click()}
            disabled={isUploadingQnr}
          >
            {isUploadingQnr
              ? <><FaRobot style={{ marginRight: "8px" }} />Comparing…</>
              : <><FaFileWord style={{ marginRight: "8px" }} />Upload Latest QNR</>}
          </button>
        </div>
      </div>
    );
  };

  // ─── AI Review Changes modal ──────────────────────────────────────────────────
  const renderReviewModal = () => {
    const pendingCount = aiChanges.filter((c) => c.status === "pending").length;
    const acceptedCount = aiChanges.filter((c) => c.status === "accepted").length;
    const rejectedCount = aiChanges.filter((c) => c.status === "rejected").length;

    // Compact one-line summary of a whole question (delete/new cards).
    const qBrief = (q) => {
      if (!q) return "";
      const title = stripHtml(q.Qtitle || "").substring(0, 90);
      return `${title || "(no title)"} · ${q.type || "?"} · ${q.rows?.length || 0} rows / ${q.columns?.length || 0} cols`;
    };

    // Field-level diff rows for a modified question - shows ONLY what
    // changed (same diff/format helpers the Change Log modal uses), so a
    // large question fits in a few lines instead of two full JSON walls.
    const renderCompactDiff = (change) => {
      const ops = diffDataGroups(
        { [change.questionKey]: change.original },
        { [change.questionKey]: change.updated }
      );
      if (!ops.length) {
        return <p className="aireview-nodiff">No visible field changes.</p>;
      }
      return (
        <div className="aireview-ops">
          {ops.map((op, i) => {
            const f = formatOp(op);
            return (
              <div key={i} className="aireview-op">
                <span className="aireview-op-label">{f.label}</span>
                <div className="aireview-op-items">
                  {f.kind === "array" ? (
                    <>
                      {(f.changed || []).map((c, j) => (
                        <div key={`c${j}`} className="aireview-op-line">
                          <span className="op-before">{c.before}</span>
                          <span className="op-arrow">→</span>
                          <span className="op-after">{c.after}</span>
                        </div>
                      ))}
                      {(f.added || []).map((t, j) => (
                        <div key={`a${j}`} className="aireview-op-line">
                          <span className="op-chip chip-add">+ {t}</span>
                        </div>
                      ))}
                      {(f.removed || []).map((t, j) => (
                        <div key={`r${j}`} className="aireview-op-line">
                          <span className="op-chip chip-del">− {t}</span>
                        </div>
                      ))}
                    </>
                  ) : f.kind === "object" ? (
                    (f.fields || []).map((fd, j) => (
                      <div key={j} className="aireview-op-line">
                        <span className="op-key">{fd.key}:</span>
                        <span className="op-before">{fd.before || "—"}</span>
                        <span className="op-arrow">→</span>
                        <span className="op-after">{fd.after || "—"}</span>
                      </div>
                    ))
                  ) : (
                    <div className="aireview-op-line">
                      <span className="op-before">{f.before || "—"}</span>
                      <span className="op-arrow">→</span>
                      <span className="op-after">{f.after || "—"}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <Modal
        isOpen={showReviewModal}
        onRequestClose={() => setShowReviewModal(false)}
        className="aireview-modal-content"
        // Overlay z-index (10060, in the CSS) must stay ABOVE the Survey
        // Preview modal (.all-lists-overlay, 1050) - this modal's portal is
        // mounted earlier in the DOM, so without a higher z-index it would
        // paint BEHIND the open Preview.
        overlayClassName="aireview-modal-overlay"
        ariaHideApp={false}
      >
        <div className="aireview-modal-header">
          <h3>
            <FaRobot style={{ marginRight: "10px", color: "#7c3aed" }} />
            Review AI Changes ({aiChanges.length})
          </h3>
          <span className="aireview-status">
            {acceptedCount} accepted · {rejectedCount} rejected · {pendingCount} pending
          </span>
          <button className="aireview-close-btn" title="Close" onClick={() => setShowReviewModal(false)}>
            <FaTimes />
          </button>
        </div>

        <div className="aireview-toolbar">
          <button className="aireview-acceptall-btn" onClick={acceptAllChanges}>
            <FaCheck /> Accept All
          </button>
          <button className="aireview-rejectall-btn" onClick={rejectAllChanges}>
            <FaTimes /> Reject All
          </button>
        </div>

        <div className="aireview-body">
          {aiChanges.map((change) => {
            const isAccepted = change.status === "accepted";
            const isRejected = change.status === "rejected";
            const isDeletion = change.updated?._deleted === true;
            const isNewQ = change.isNew === true;
            const isConflict = change.conflict === true;

            const acceptLabel = isConflict ? "✓ Use QNR Version"
              : isDeletion ? "🗑 Accept Deletion"
                : isNewQ ? "➕ Add Question"
                  : "✓ Accept";
            const rejectLabel = isConflict ? "✗ Keep My Edit"
              : isDeletion ? "↩ Keep Question"
                : isNewQ ? "✗ Skip"
                  : "✗ Reject";

            const headerState = isAccepted ? "is-accepted"
              : isRejected ? "is-rejected"
                : isConflict ? "is-conflict"
                  : isDeletion ? "is-deletion"
                    : isNewQ ? "is-new"
                      : "";

            return (
              <div key={`${change.questionKey}_${change.isNew ? "new" : "mod"}`} className="aireview-card">
                <div className={`aireview-card-header ${headerState}`}>
                  <div>
                    <h4 className="aireview-card-title">
                      <span>{change.questionKey}</span>
                      {!isNewQ && change.original?.Qtitle && (
                        <span className="aireview-card-subtitle"
                          dangerouslySetInnerHTML={{
                            __html: (change.original.Qtitle || "").replace(/<[^>]+>/g, "").substring(0, 60),
                          }}
                        />
                      )}
                      {isNewQ && (change.insertAfterKey || change.insertBeforeKey) && (
                        <span className="aireview-insert-badge">
                          {change.insertAfterKey
                            ? `➕ Insert after ${change.insertAfterKey}`
                            : `➕ Insert before ${change.insertBeforeKey}`}
                        </span>
                      )}
                    </h4>
                    <div className="aireview-badges">
                      {isNewQ && !isAccepted && !isRejected && (
                        <span className="aireview-badge badge-new">✨ New Question</span>
                      )}
                      {isConflict && !isAccepted && !isRejected && (
                        <span className="aireview-badge badge-conflict">⚠ Conflict — both changed</span>
                      )}
                      {isDeletion && !isConflict && !isAccepted && !isRejected && (
                        <span className="aireview-badge badge-deletion">
                          {change.source === "qnr" ? "🗑 Missing from new QNR" : "🗑 Deletion Proposed"}
                        </span>
                      )}
                      {isAccepted && (
                        <span className="aireview-badge badge-accepted">
                          <FaCheck />
                          {isConflict ? "QNR Version Used" : isDeletion ? "Deletion Accepted" : isNewQ ? "Will Be Added" : "Accepted"}
                        </span>
                      )}
                      {isRejected && (
                        <span className="aireview-badge badge-rejected">
                          <FaTimes />
                          {isConflict ? "Kept Your Edit" : isDeletion ? "Kept" : isNewQ ? "Skipped" : "Rejected"}
                        </span>
                      )}
                    </div>
                    {isDeletion && change.suggestedNewKey && (
                      <div className="aireview-insert-badge" style={{ marginTop: "4px" }}>
                        Possibly renamed to {change.suggestedNewKey}? The new doc has a similarly worded question.
                      </div>
                    )}
                  </div>
                  <div className="aireview-card-actions">
                    <button
                      className={`aireview-accept-btn${isAccepted ? " is-on" : ""}`}
                      onClick={() => acceptChange(change.questionKey)}
                    >
                      {acceptLabel}
                    </button>
                    <button
                      className={`aireview-reject-btn${isRejected ? " is-on" : ""}`}
                      onClick={() => rejectChange(change.questionKey)}
                    >
                      {rejectLabel}
                    </button>
                  </div>
                </div>

                <div className="aireview-card-body">
                  {isDeletion ? (
                    <div>
                      <div className="aireview-note note-red">
                        <strong>🗑️ Question to be deleted</strong>
                        {qBrief(change.original)} — accepting will permanently remove it.
                      </div>
                      <details className="aireview-json-details" open>
                        <summary>Full JSON (click to collapse)</summary>
                        <pre className="aireview-pre pre-red">
                          {JSON.stringify(change.original, null, 2)}
                        </pre>
                      </details>
                    </div>
                  ) : isNewQ ? (
                    <div>
                      <div className="aireview-note note-green">
                        <strong>✨ Proposed new question</strong>
                        {qBrief(change.updated)}
                      </div>
                      <details className="aireview-json-details" open>
                        <summary>Full JSON (click to collapse)</summary>
                        <pre className="aireview-pre pre-green">
                          {JSON.stringify(change.updated, null, 2)}
                        </pre>
                      </details>
                    </div>
                  ) : (
                    <>
                      {isConflict && (
                        <div className="aireview-note" style={{ background: "#fefce8", color: "#854d0e" }}>
                          <strong>⚠️ Both your edit and the new QNR changed this question, differently.</strong>{" "}
                          "Use QNR Version" applies the document's text; "Keep My Edit" leaves your version untouched.
                        </div>
                      )}
                      {renderCompactDiff(change)}
                      {isConflict && (
                        <details className="aireview-json-details">
                          <summary>Last known QNR version (before either side changed — click to expand)</summary>
                          <pre className="aireview-pre">
                            {JSON.stringify(change.baseValue, null, 2)}
                          </pre>
                        </details>
                      )}
                      <details className="aireview-json-details" open>
                        <summary>Full JSON diff — compare mode (click to collapse)</summary>
                        <div className="aireview-sdiff">
                          <div className="sdiff-colhead">
                            <span className="sdiff-colhead-left">{isConflict ? "Your Edit" : "Original"}</span>
                            <span className="sdiff-colhead-right">{isConflict ? "New QNR" : "AI Proposed"}</span>
                          </div>
                          {splitDiffRows(
                            JSON.stringify(change.original, null, 2),
                            JSON.stringify(change.updated, null, 2)
                          ).map((row, i) =>
                            row.type === "skip" ? (
                              <div key={i} className="sdiff-skip">
                                ⋯ {row.count} unchanged line{row.count !== 1 ? "s" : ""} ⋯
                              </div>
                            ) : (
                              <div key={i} className="sdiff-row">
                                <div
                                  className={`sdiff-cell sdiff-left ${
                                    row.type === "same" ? "cell-same"
                                      : row.left === undefined ? "cell-empty"
                                        : "cell-del"
                                  }`}
                                >
                                  {row.left !== undefined && (
                                    <>
                                      <span className="sdiff-sign">{row.type === "same" ? " " : "−"}</span>
                                      <span className="sdiff-text">{row.left}</span>
                                    </>
                                  )}
                                </div>
                                <div
                                  className={`sdiff-cell sdiff-right ${
                                    row.type === "same" ? "cell-same"
                                      : row.right === undefined ? "cell-empty"
                                        : "cell-add"
                                  }`}
                                >
                                  {row.right !== undefined && (
                                    <>
                                      <span className="sdiff-sign">{row.type === "same" ? " " : "+"}</span>
                                      <span className="sdiff-text">{row.right}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </details>

                      <details className="aireview-json-details">
                        <summary>Raw JSON — no diff, for copy/paste (click to expand)</summary>
                        <div className="aireview-diff-grid">
                          <div>
                            <div className="aireview-diff-label label-red">Original</div>
                            <pre className="aireview-pre pre-red">
                              {JSON.stringify(change.original, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <div className="aireview-diff-label label-green">AI Proposed</div>
                            <pre className="aireview-pre pre-green">
                              {JSON.stringify(change.updated, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </details>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="aireview-modal-footer">
          <button className="aireview-cancel-btn" onClick={() => setShowReviewModal(false)}>
            Close
          </button>
          <button
            className="aireview-apply-btn"
            onClick={applySelectedChanges}
            disabled={acceptedCount === 0}
          >
            <FaCheck />
            Apply {acceptedCount > 0 ? `${acceptedCount} ` : ""}
            Selected Change{acceptedCount !== 1 ? "s" : ""}
          </button>
        </div>
      </Modal>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────────
  return (
    <>
      <CommonButton
        symbol={faEye}
        title="Preview Survey"
        onClick={() => { setIsPreviewOpen(true); setAiSubmitAttempts(0); }}
      />

      {/* AI Review Changes modal */}
      {renderReviewModal()}

      {/* Preview Modal */}
      <Modal
        isOpen={isPreviewOpen}
        onRequestClose={() => { setIsPreviewOpen(false); setAiSubmitAttempts(0); }}
        className="all-lists-modal"
        overlayClassName="all-lists-overlay"
        ariaHideApp={false}
        style={{
          overlay: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          content: {
            position: "relative",
            top: "auto",
            left: "auto",
            right: "auto",
            bottom: "auto",
            transform: "none",
            margin: "0",
            width: "95%",
            height: "95%",
            maxWidth: "1600px",
            borderRadius: "12px",
            padding: "0",
            overflow: "hidden",
          },
        }}
      >
        <div className="preview-modal">
          {/* Header */}
          <div className="preview-header">
            <h2>
              <FaEye style={{ marginRight: "10px", color: "#2563eb" }} />
              Survey Preview –{" "}
              <span>{projectName || surveyName || surveyFileName || "Untitled Survey"}</span>
            </h2>
            <div className="preview-header-actions">
              {/* Preview type selector */}
              <div className="preview-tabsv1">
                <select
                  value={previewType === "export" ? `export_${selectedExport}` : previewType}
                  onChange={(e) => handlePreviewChange(e.target.value)}
                >
                  <optgroup label="Survey Views">
                    <option value="questions"><FaList style={{ marginRight: "6px" }} />Questions</option>
                    <option value="statistics"><FaChartBar style={{ marginRight: "6px" }} />Statistics</option>
                    <option value="json"><FaCode style={{ marginRight: "6px" }} />Raw JSON</option>
                    <option value="survey"><FaHome style={{ marginRight: "6px" }} />Survey</option>
                    <option value="translation"><FaLanguage style={{ marginRight: "6px" }} />Translation</option>
                  </optgroup>
                  {Object.keys(exportedFiles).length > 0 && (
                    <optgroup label="Exported Files">
                      {Object.entries(exportedFiles).map(([type, data]) => (
                        <option key={type} value={`export_${type}`}>
                          <FaFileAlt style={{ marginRight: "6px" }} />
                          {type.toUpperCase()} ({data.extension}) –{" "}
                          {new Date(data.timestamp).toLocaleDateString()}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>

              {/* Document split view */}
              {questionnaireFileId && (
                <button
                  onClick={toggleSplitView}
                  disabled={isLoadingDocx}
                  className={`ph-action-btn info ${showSplitView ? "is-active" : ""}`}
                >
                  {showSplitView
                    ? <><FaCheck /> Split View</>
                    : <><FaFileWord /> Show Doc</>}
                </button>
              )}

              {/* Undo / Redo (Raw JSON editing) */}
              {previewType === "json" && (
                <>
                  <CommonButton
                    symbol={faRotateLeft}
                    variant="secondary"
                    title="Undo (Ctrl+Z)"
                    disabled={!canUndo}
                    onClick={() => undo()}
                  />
                  <CommonButton
                    symbol={faRotateRight}
                    variant="secondary"
                    title="Redo (Ctrl+Y)"
                    disabled={!canRedo}
                    onClick={() => redo()}
                  />
                </>
              )}

              {/* Save button */}
              <button
                onClick={handleSaveSurvey}
                disabled={isSaving || !hasUnsavedChanges}
                className={`ph-action-btn info ${!isSaving ? "is-active" : ""}`}
                style={{
                  opacity: isSaving || !hasUnsavedChanges ? 0.6 : 1,
                  cursor: isSaving || !hasUnsavedChanges ? "not-allowed" : "pointer",
                }}
              >
                {isSaving
                  ? <><span className="loading-spinner" /> Saving...</>
                  : hasUnsavedChanges
                    ? <><FaSave /> Save Changes</>
                    : <><FaCheck /> Saved</>}
              </button>

              {/* Voice */}
              <button
                onClick={toggleListening}
                className={`voice-button ${isListening ? "active" : ""}`}
                title="Voice Commands (e.g., 'bold title in question 1')"
              >
                {isListening ? <FaStop /> : <FaMicrophone />}
              </button>
              {transcript && (
                <div className="transcript-text">
                  Last: {transcript}
                </div>
              )}

              {/* Close button - same style as AllListsModal */}
              <button
                onClick={() => { setIsPreviewOpen(false); setAiSubmitAttempts(0); }}
                className="all-close-button"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Main content */}
          <div
            ref={containerRef}
            className="preview-content-wrapper"
          >
            {/* Survey preview panel */}
            <div
              className="preview-content"
              style={{
                width: showSplitView ? `${splitRatio}%` : "100%",
                transition: isDragging ? "none" : "width 0.3s ease",
              }}
            >
              {previewType === "questions" && renderQuestionsPreview()}
              {previewType === "statistics" && renderStatisticsPreview()}
              {previewType === "json" && renderJSONPreview()}
              {previewType === "export" && renderExportPreview()}
              {previewType === "translation" && renderTranslationPreview()}
              {previewType === "survey" && (
                <div className="survey-view-container">
                  <div className="survey-language-bar">
                    <FaLanguage />
                    <select
                      className="survey-language-select"
                      value={activeTranslationLang || ""}
                      onChange={(e) => setActiveTranslationLang(e.target.value || null)}
                    >
                      <option value="">Original</option>
                      {supportedLanguages.map((label) => (
                        <option key={label} value={label}>
                          {label}{!translationsByLang[label] ? " (not yet translated)" : ""}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="survey-language-manage-btn"
                      onClick={() => {
                        setManageSelectedLanguages(supportedLanguages); // pre-check what's already configured
                        setAiSelectedLanguages([]); // AI section always starts unselected
                        setManageLanguageSearch("");
                        setAiLanguageSearch("");
                        setSelectedExport(null);
                        setPreviewType("translation");
                      }}
                    >
                      Manage translations…
                    </button>
                  </div>
                  <div className="survey-view-content">
                    {renderSurveyView(applyTranslation(currentQuestion))}
                  </div>
                  <div className="survey-view-footer">
                    <button
                      onClick={handleBack}
                      disabled={isFirst}
                      className="survey-nav-btn back"
                    >
                      <FaArrowLeft /> Back
                    </button>
                    <span className="survey-counter">
                      {currentIndex + 1} / {Object.keys(sdataGroup).length}
                    </span>
                    <button
                      onClick={handleNext}
                      disabled={isLast}
                      className={`survey-nav-btn next ${isLast ? "end" : ""}`}
                    >
                      {isLast ? <><FaCheck /> End of Survey</> : <>Next <FaArrowRight /></>}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* DOCX divider */}
            {showSplitView && (
              <div
                onMouseDown={handleMouseDown}
                className="divider-handle"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#3b82f6";
                }}
                onMouseLeave={(e) => {
                  if (!isDragging)
                    e.currentTarget.style.backgroundColor = "#e2e8f0";
                }}
              >
                <div className="divider-grip">⋮</div>
              </div>
            )}

            {/* DOCX preview panel */}
            {showSplitView && (
              <div
                className="docx-preview-panel"
                style={{
                  width: `${100 - splitRatio}%`,
                  transition: isDragging ? "none" : "width 0.3s ease",
                }}
              >
                {isDragging && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 9999,
                      cursor: "col-resize",
                    }}
                  />
                )}
                <div style={{ flex: 1, overflow: "hidden" }}>
                  {embedUrl ? (
                    <iframe
                      src={embedUrl}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      title="Document Preview"
                      style={{ border: "none" }}
                    />
                  ) : (
                    <div className="preview-empty" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: "12px" }}>
                      {isLoadingDocx
                        ? <><span className="loading-spinner" style={{ width: "32px", height: "32px", borderWidth: "3px" }} /> Loading document...</>
                        : <><FaFileWord size={48} style={{ opacity: 0.3 }} /> No preview available</>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal >
    </>
  );
};

export default SurveyPreview;